/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperProducts } from '@/scapi';
import type { NavCategory, NavTopCategory } from '@/lib/navigation/types';
import { parseFeaturedLinks } from '@/lib/navigation/featured-links';
import { routes, routeHref } from '@/route-paths';

type ScapiCategory = ShopperProducts.schemas['Category'];

function optionalString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

export function toNavCategory(category: ScapiCategory): NavCategory {
    return {
        id: category.id,
        name: category.name ?? category.id,
        // Site/locale prefixing happens in the `Link` wrapper, so this stays unprefixed.
        url: routeHref(routes.category, { categoryId: category.id }),
        children: (category.categories ?? []).map(toNavCategory),
        across: category.c_across === true,
        informationalText: optionalString(category.c_informationalText),
        icon: optionalString(category.c_navIcon),
    };
}

export function toNavTopCategory(category: ScapiCategory): NavTopCategory {
    return {
        ...toNavCategory(category),
        featured: category.c_featured === true,
        featuredLinks: parseFeaturedLinks(category.c_featuredLinks),
    };
}

/**
 * Level 2 arrives with the root request, level 3/4 through the deferred store keyed by
 * level 2 (see the `_app` loader). Both navigations need the two stitched together.
 */
export function toNavTreeWithDeferred(
    category: ScapiCategory,
    subCategories: ReadonlyMap<string, ScapiCategory>
): NavTopCategory {
    return {
        ...toNavTopCategory(category),
        children: (category.categories ?? []).map((child) => toNavCategory(subCategories.get(child.id) ?? child)),
    };
}

/**
 * True when `c_featuredLinks` holds something the merchandiser meant as links but nothing
 * survived parsing — the one case worth logging, since the column silently disappears.
 */
export function hasUnparsableFeaturedLinks(category: ScapiCategory): boolean {
    return (
        optionalString(category.c_featuredLinks) !== undefined &&
        parseFeaturedLinks(category.c_featuredLinks).length === 0
    );
}
